// Luminance <-> code mapping for the station's display (mirror of backend/vtlite/display.py).
// A "code" is the normalized value the renderer writes (0..1); "rel" is luminance as a
// fraction of display white.

export const srgbEotf = (code: number): number =>
  code <= 0.04045 ? code / 12.92 : ((code + 0.055) / 1.055) ** 2.4;

export const srgbOetf = (rel: number): number =>
  rel <= 0.0031308 ? 12.92 * rel : 1.055 * rel ** (1 / 2.4) - 0.055;

export function nominalSrgbLut(size = 4096): number[] {
  return Array.from({ length: size }, (_, i) => srgbEotf(i / (size - 1)));
}

/** Invert a monotone forward LUT (rel luminance at evenly spaced codes). */
export function codeForRel(lut: ArrayLike<number>, targetRel: number): number {
  const n = lut.length;
  const lo0 = lut[0]!;
  const hi0 = lut[n - 1]!;
  const target = Math.min(Math.max(targetRel, lo0), hi0);
  // first index with lut[i] >= target (numpy searchsorted, side="left")
  let a = 0;
  let b = n;
  while (a < b) {
    const m = (a + b) >>> 1;
    if (lut[m]! < target) a = m + 1;
    else b = m;
  }
  const i = a;
  if (i <= 0) return 0;
  if (i >= n) return 1;
  const lo = lut[i - 1]!;
  const hi = lut[i]!;
  const frac = hi === lo ? 0 : (target - lo) / (hi - lo);
  return (i - 1 + frac) / (n - 1);
}

export interface ContrastCodes {
  backgroundCode: number;
  letterCode: number;
  /** Weber contrast actually produced after quantization to the output depth. */
  presentedWeber: number;
}

/** Codes for a dark letter of Weber contrast `weber` on a background of `backgroundRel`. */
export function contrastCodes(
  lut: ArrayLike<number>,
  backgroundRel: number,
  weber: number,
  outputBits: number,
): ContrastCodes {
  const bgCode = codeForRel(lut, backgroundRel);
  const letterCode = codeForRel(lut, backgroundRel * (1 - weber));
  const q = (c: number) => (outputBits >= 16 ? c : Math.round(c * (2 ** outputBits - 1)) / (2 ** outputBits - 1));
  const relAt = (code: number) => {
    const x = code * (lut.length - 1);
    const i = Math.min(lut.length - 2, Math.floor(x));
    const f = x - i;
    return lut[i]! * (1 - f) + lut[i + 1]! * f;
  };
  const lb = relAt(q(bgCode));
  const ll = relAt(q(letterCode));
  return { backgroundCode: bgCode, letterCode, presentedWeber: lb > 0 ? (lb - ll) / lb : 0 };
}

// ------------------------------------------------------------- colour stimuli ---------
// Colours are rel per channel (linear fraction of each primary). The station's LUT is
// measured on grey, so each channel is assumed to follow the same curve; luminance uses
// the nominal sRGB / Rec. 709 weights (the display's primaries are not measured).

export type RGB3 = [number, number, number];
export const LUMA_WEIGHTS: RGB3 = [0.2126, 0.7152, 0.0722];

/** Relative luminance; exactly the channel value on grey (so grey matches the grey path bit for bit). */
export const luminanceOf = (rgb: RGB3): number =>
  rgb[0] === rgb[1] && rgb[1] === rgb[2] ? rgb[0]
    : LUMA_WEIGHTS[0] * rgb[0] + LUMA_WEIGHTS[1] * rgb[1] + LUMA_WEIGHTS[2] * rgb[2];

export const quantizeCode = (code: number, bits: number): number =>
  bits >= 16 ? code : Math.round(code * (2 ** bits - 1)) / (2 ** bits - 1);

export function relAtCode(lut: ArrayLike<number>, code: number): number {
  const x = Math.min(Math.max(code, 0), 1) * (lut.length - 1);
  const i = Math.min(lut.length - 2, Math.floor(x));
  const f = x - i;
  return lut[i]! * (1 - f) + lut[i + 1]! * f;
}

export const codesForRgb = (lut: ArrayLike<number>, rgb: RGB3): RGB3 =>
  [codeForRel(lut, rgb[0]), codeForRel(lut, rgb[1]), codeForRel(lut, rgb[2])];

/** The colour actually produced by these codes after quantization (rel per channel). */
export const presentedRgb = (lut: ArrayLike<number>, codes: RGB3, bits: number): RGB3 =>
  [relAtCode(lut, quantizeCode(codes[0], bits)), relAtCode(lut, quantizeCode(codes[1], bits)),
    relAtCode(lut, quantizeCode(codes[2], bits))];

/** How a test's letter colour follows its contrast `c` (0..1):
 *  fixed — the letter colour itself (acuity); luminance — the background darkened by c
 *  (Weber luminance contrast c); toward_letter — c of the way from background to letter colour. */
export type LetterRule = "fixed" | "luminance" | "toward_letter";

export function letterRgbFor(rule: LetterRule, bg: RGB3, letter: RGB3, c: number): RGB3 {
  if (rule === "fixed") return [...letter] as RGB3;
  if (rule === "luminance") return [bg[0] * (1 - c), bg[1] * (1 - c), bg[2] * (1 - c)];
  return [bg[0] + c * (letter[0] - bg[0]), bg[1] + c * (letter[1] - bg[1]), bg[2] + c * (letter[2] - bg[2])];
}

export interface ColourCodes {
  backgroundRgb: RGB3;
  letterRgb: RGB3;
  /** Grey-equivalent codes (equal to the channel codes on a grey background). */
  backgroundCode: number;
  letterCode: number;
  /** Weber luminance contrast actually produced (after quantization). */
  presentedWeber: number;
  /** For toward_letter: the fraction of the background-to-letter difference produced. */
  presentedModulation: number | null;
}

export function colourCodes(lut: ArrayLike<number>, rule: LetterRule, bg: RGB3, letter: RGB3, c: number,
  bits: number): ColourCodes {
  const target = letterRgbFor(rule, bg, letter, c);
  const bgCodes = codesForRgb(lut, bg);
  const lCodes = codesForRgb(lut, target);
  const pb = presentedRgb(lut, bgCodes, bits);
  const pl = presentedRgb(lut, lCodes, bits);
  const yb = luminanceOf(pb);
  const yl = luminanceOf(pl);
  let modulation: number | null = null;
  if (rule === "toward_letter") {
    const d = [letter[0] - bg[0], letter[1] - bg[1], letter[2] - bg[2]];
    const dd = d[0]! ** 2 + d[1]! ** 2 + d[2]! ** 2;
    const dq = [pl[0] - pb[0], pl[1] - pb[1], pl[2] - pb[2]];
    modulation = dd > 0 ? Math.max(0, (dq[0]! * d[0]! + dq[1]! * d[1]! + dq[2]! * d[2]!) / dd) : 0;
  }
  return {
    backgroundRgb: bgCodes, letterRgb: lCodes,
    backgroundCode: codeForRel(lut, luminanceOf(bg)), letterCode: codeForRel(lut, luminanceOf(target)),
    presentedWeber: yb > 0 ? (yb - yl) / yb : 0, presentedModulation: modulation,
  };
}

/** Codes of `base` mixed toward `toward` by weight k (in rel), for the edge jitter. */
export function mixCodes(lut: ArrayLike<number>, base: RGB3, toward: RGB3, k: number): RGB3 {
  return codesForRgb(lut, [base[0] + k * (toward[0] - base[0]), base[1] + k * (toward[1] - base[1]),
    base[2] + k * (toward[2] - base[2])]);
}
